import { useParams } from "react-router-dom";

import EmptyPanel from "components/EmptyPanel";
import PanelsLayout from "components/PanelsLayout";

import { ELegalActBox } from "dto/LocalLaw/ILocalLaw";

import ActsView from "./ActsView";
import PermissionsView from "./PermissionsView";
import RequestsView from "./RequestsView";
import styles from "./styles.module.scss";

// Prawo miejscowe — port modułu Kwantum.SL.LocalLaw z Magistratu.
//
// Widoki pod jedną pozycją menu (odpowiedniki pozycji z Workspace/ViewModel/Menu.cs
// oraz list LocalLaw* z Kwantum.Documents.List):
//   /prawo-miejscowe/projekty     → „Projekty aktów" (LocalLawProjectsList),
//   /prawo-miejscowe/akceptacja   → projekty przekazane do akceptacji,
//   /prawo-miejscowe/publikacja   → „Do publikacji" (SignAndPublish) + ZIPX — trafiają
//                                    tu akty od chwili akceptacji, nie dopiero po
//                                    formalnym przekazaniu (decyzja usera),
//   /prawo-miejscowe/uchwalone    → uchwalone, z nadanym numerem (LocalLawToAdopt),
//   /prawo-miejscowe/wnioski      → wnioski o ogłoszenie w dzienniku urzędowym (UPE),
//   /prawo-miejscowe/obowiazujace → akty obowiązujące (LocalLawAdopted),
//   /prawo-miejscowe/uprawnienia  → flagi LocalLawAccess dla pracowników.
//
// Treść aktu powstaje w Wordzie: wtyczka rozpoznaje dokument o typie „akt prawny"
// i pokazuje pełną wstążkę — metrykę, jednostki redakcyjne, strukturę i XML.

const VIEWS: Record<string, { title: string; element: JSX.Element }> = {
  akceptacja: {
    title: "Do akceptacji",
    element: (
      <ActsView
        box={ELegalActBox.ToApprove}
        title="Do akceptacji"
        emptyText="Nie ma projektów czekających na akceptację."
      />
    ),
  },
  publikacja: {
    title: "Do publikacji",
    element: (
      <ActsView
        box={ELegalActBox.ToPublish}
        title="Do publikacji"
        emptyText="Żaden akt nie został jeszcze zaakceptowany."
        allowPublicationRequest
      />
    ),
  },
  uchwalone: {
    title: "Uchwalone",
    element: (
      <ActsView
        box={ELegalActBox.Adopted}
        title="Uchwalone"
        emptyText="Żaden akt nie ma jeszcze nadanego numeru i daty uchwalenia."
      />
    ),
  },
  wnioski: {
    title: "Wnioski o publikację",
    element: <RequestsView />,
  },
  obowiazujace: {
    title: "Akty obowiązujące",
    element: (
      <ActsView
        box={ELegalActBox.InForce}
        title="Akty obowiązujące"
        emptyText="Brak aktów obowiązujących."
      />
    ),
  },
  uprawnienia: {
    title: "Uprawnienia",
    element: <PermissionsView />,
  },
};

const LocalLawModule = () => {
  const { view } = useParams();

  // Wyliczane z trasy, a nie trzymane w stanie — przy przejściu między widokami
  // React zachowuje ten sam komponent i inicjalizator useState by się nie powtórzył.
  const current = (view && VIEWS[view]) ?? {
    title: "Projekty",
    element: (
      <ActsView
        box={ELegalActBox.Projects}
        title="Projekty aktów"
        emptyText="Nie masz jeszcze żadnego projektu aktu. Zacznij od przycisku „Nowy projekt”."
        allowCreate
      />
    ),
  };

  return (
    <PanelsLayout
      title="Prawo miejscowe"
      subTitle={current.title}
      disableSearch
      leftClassName={styles.leftPanel}
      leftPanel={<div className={styles.panel}>{current.element}</div>}
      hideRight
      rightPanel={<EmptyPanel />}
    />
  );
};

export default LocalLawModule;
